/**
 * Blox Component Wrappers
 *
 * Re-exports @ox/blox components with visit-specific defaults and convenience wrappers.
 * This provides a consistent interface across all form sections.
 */
/* eslint-disable react/jsx-no-bind */

import {
  Input as BloxInput,
  Select as BloxSelect,
  Textarea as BloxTextarea,
  Checkbox as BloxCheckbox,
  RadioButton as BloxRadioButton,
  Button as BloxButton,
  type Options,
} from '@ox/blox';
import { useForm } from '@ox/visits-ui/context/FormContext';
import type { ComponentChildren } from 'preact';

/**
 * Input wrapper with visit defaults
 */
export function Input(props: {
  label: string;
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  onInput?: (e: Event) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  [key: string]: unknown;
}) {
  const { onChange, onInput, ...rest } = props;
  const { errors, viewMode } = useForm();

  // Handle both onChange with value and onInput with event
  const handleInput = (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    if (onChange) {
      onChange(target.value);
    }
    if (onInput) {
      onInput(e);
    }
  };

  return <BloxInput {...rest} onInput={handleInput} errorMessage={errors[props.name]} readOnly={viewMode} />;
}

/**
 * Select wrapper with visit defaults
 */
export function Select(props: {
  label: string;
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  options: Options;
  required?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  [key: string]: unknown;
}) {
  const { onChange, options, value, ...rest } = props;
  const { errors, formData, viewMode } = useForm();

  // Map value to selected in options
  const mappedOptions = options.map((opt) => {
    if ('options' in opt) {
      // OptGroup
      return {
        ...opt,
        options: opt.options.map((subOpt) => ({
          ...subOpt,
          selected: subOpt.value === value,
        })),
      };
    } 
    // Option
    return {
      ...opt,
      selected: opt.value === value,
    };
    
  });

  const handleChange = (e: Event) => {
    const target = e.currentTarget as HTMLSelectElement | HTMLInputElement;
    if (onChange) {
      onChange(target.value);
    }
  };

  const handleInput = (e: Event) => {
    const target = e.currentTarget as HTMLSelectElement | HTMLInputElement;
    if (onChange) {
      onChange(target.value);
    }
  };

  if (viewMode) {
    return <BloxInput {...rest} readOnly={viewMode} value={formData[props.name]} />;
  }

  return <BloxSelect {...rest} options={mappedOptions} onChange={handleChange} onInput={handleInput} errorMessage={errors[props.name]} />;
}

/**
 * Textarea wrapper with visit defaults
 */
export function Textarea(props: {
  label: string;
  name: string;
  value?: string;
  onChange?: (value: string) => void;
  onInput?: (e: Event) => void;
  placeholder?: string;
  required?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  rows?: number;
  [key: string]: unknown;
}) {
  const { onChange, onInput, ...rest } = props;
  const { errors, viewMode } = useForm();

  // Handle both onChange with value and onInput with event
  const handleInput = (e: Event) => {
    const target = e.currentTarget as HTMLTextAreaElement;
    if (onChange) {
      onChange(target.value);
    }
    if (onInput) {
      onInput(e);
    }
  };

  return <BloxTextarea {...rest} onInput={handleInput} errorMessage={errors[props.name]} readOnly={viewMode} />;
}

/**
 * Checkbox wrapper with visit defaults
 */
export function Checkbox(props: {
  label: ComponentChildren;
  name: string;
  value: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  required?: boolean;
  disabled?: boolean;
  [key: string]: unknown;
}) {
  const { onChange, ...rest } = props;
  const { viewMode } = useForm();

  const handleChange = (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    if (onChange) {
      onChange(target.checked);
    }
  };
  
  return <BloxCheckbox {...rest} onChange={handleChange} readOnly={viewMode} />;
}

/**
 * RadioButton wrapper with visit defaults
 */
export function RadioButton(props: {
  label: ComponentChildren;
  name: string;
  value: string;
  checked?: boolean;
  onChange?: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  [key: string]: unknown;
}) {
  const { onChange, ...rest } = props;
  const { errors, viewMode } = useForm();

  const handleChange = (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    if (onChange && target.checked) {
      onChange(target.value);
    }
  };

  return <BloxRadioButton {...rest} onChange={handleChange} errorMessage={errors[props.name]} readOnly={viewMode} />;
}

/**
 * Button wrapper with visit defaults
 */
export function Button(props: {
  children: ComponentChildren;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  className?: string;
  [key: string]: unknown;
}) {
  return <BloxButton {...props} />;
}

/**
 * Re-export other Blox components as-is
 */
export { SecondaryButton, TertiaryButton, LinkButton, CopyButton } from '@ox/blox';

export type { Option, Options } from '@ox/blox';
